// KnowledgeFeedDetailPage.tsx — DEM-56
//
// Detail view for one ingested YouTube video. Structured render path —
// operational data with an embedded player, not a KB document. The
// per-video KB page (when present) still lives under
// kb-doc/myjarvis-os/knowledge-feed/<slug> for the graph; we cross-link
// to it from here.
//
// Layout: hero with metadata, embedded player + outbound link, the full
// LLM summary, then the raw transcript (in a scrollable card so it doesn't
// dominate the page). Light-mode is the layout default — /knowledge-feed
// is in Layout's SKY_BLUE_ROUTES.

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft,
  ExternalLink,
  Loader2,
  AlertTriangle,
  FileText,
  Sparkles,
  Brain,
  Lightbulb,
  Hash,
  Tag,
  Youtube,
} from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureT as T } from "../blueprint/ArchitectureBlocks";

type KnowledgeFeedDetail = {
  video_id: string;
  title: string | null;
  channel: string | null;
  url: string | null;
  duration_sec: number | null;
  is_short: boolean | null;
  watched_pull_at: string;
  has_transcript: boolean;
  transcript: string | null;
  summary: string | null;
  principles: { title: string; snippet: string }[] | null;
  tags: string[] | null;
  topic: string | null;
  is_ai_related: boolean | null;
  is_substantive: boolean | null;
  kb_page_slug: string | null;
  raw_meta: unknown;
  agent: string;
  ingest_error: string | null;
  created_at: string;
  updated_at: string;
};

function formatDuration(sec: number | null): string {
  if (!sec || sec <= 0) return "";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function watchUrl(row: KnowledgeFeedDetail): string {
  return row.url ?? `https://www.youtube.com/watch?v=${row.video_id}`;
}

function Hero({ row }: { row: KnowledgeFeedDetail }) {
  const duration = formatDuration(row.duration_sec);
  const watched = new Date(row.watched_pull_at).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
        color: T.skyDark, textTransform: "uppercase", marginBottom: 14,
      }}>
        Knowledge Feed · YouTube
      </div>
      <h1 style={{
        fontSize: 30, fontWeight: 800, color: T.ink,
        margin: "0 0 14px", letterSpacing: "-0.02em", lineHeight: 1.2,
        maxWidth: 880,
      }}>
        {row.title ?? row.video_id}
      </h1>
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
        fontSize: 13, color: T.ink2,
      }}>
        {row.channel && row.channel !== "NA" ? (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            color: T.ink2, fontWeight: 600,
          }}>
            <Youtube style={{ width: 14, height: 14, color: T.red }} />
            {row.channel}
          </span>
        ) : null}
        {duration ? (
          <span style={{ color: T.ink3 }}>{duration}</span>
        ) : null}
        <span style={{ color: T.ink3 }}>Watched {watched}</span>
        {row.is_short ? (
          <span style={{
            background: T.plumSoft, color: T.plum,
            border: `1px solid ${T.plum}`,
            fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
            textTransform: "uppercase", padding: "2px 8px", borderRadius: 999,
          }}>Short</span>
        ) : null}
        {row.is_substantive === true ? (
          <span style={{
            background: T.greenSoft, color: T.green,
            border: `1px solid ${T.green}`,
            fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
            textTransform: "uppercase", padding: "2px 8px", borderRadius: 999,
          }}>Substantive</span>
        ) : null}
        {row.is_ai_related === true ? (
          <span style={{
            background: T.skySoft, color: T.skyDark,
            border: `1px solid ${T.sky}`,
            fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
            textTransform: "uppercase", padding: "2px 8px", borderRadius: 999,
          }}>AI</span>
        ) : null}
      </div>
    </div>
  );
}

function Embed({ videoId }: { videoId: string }) {
  return (
    <div style={{
      position: "relative",
      width: "100%",
      aspectRatio: "16 / 9",
      background: T.bg2,
      border: `1px solid ${T.line}`,
      borderRadius: 12,
      overflow: "hidden",
      marginBottom: 24,
    }}>
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}`}
        title="YouTube video player"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
      />
    </div>
  );
}

function SectionTitle({ icon: Icon, text }: { icon: React.ComponentType<{ style?: React.CSSProperties }>; text: string }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      margin: "32px 0 12px",
    }}>
      <Icon style={{ width: 16, height: 16, color: T.skyDark }} />
      <h2 style={{
        fontSize: 13, fontWeight: 700, color: T.skyDark,
        margin: 0, textTransform: "uppercase", letterSpacing: "0.08em",
      }}>{text}</h2>
    </div>
  );
}

function TagsRow({ topic, tags }: { topic: string | null; tags: string[] | null }) {
  if (!topic && (!tags || tags.length === 0)) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 4 }}>
      {topic ? (
        <span style={{
          fontSize: 11, fontWeight: 700, color: T.skyDark,
          background: T.skySoft, border: `1px solid ${T.sky}`,
          padding: "3px 10px", borderRadius: 999,
          textTransform: "uppercase", letterSpacing: "0.06em",
          display: "inline-flex", alignItems: "center", gap: 4,
        }}>
          <Hash style={{ width: 11, height: 11 }} />
          {topic}
        </span>
      ) : null}
      {(tags ?? []).map((t) => (
        <span key={t} style={{
          fontSize: 12, color: T.ink2,
          background: T.white, border: `1px solid ${T.line}`,
          padding: "2px 10px", borderRadius: 999,
          display: "inline-flex", alignItems: "center", gap: 4,
        }}>
          <Tag style={{ width: 10, height: 10, color: T.ink3 }} />
          {t}
        </span>
      ))}
    </div>
  );
}

export function KnowledgeFeedDetailPage() {
  const { videoId = "" } = useParams<{ videoId: string }>();
  const api = useApi();
  const [row, setRow] = useState<KnowledgeFeedDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!videoId) return;
    setError(null);
    setRow(null);
    (async () => {
      try {
        const res = await api(`/api/knowledge-feed/${videoId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as KnowledgeFeedDetail;
        if (!cancelled) setRow(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  return (
    <div style={{
      fontFamily: "Inter, sans-serif",
      boxSizing: "border-box",
      padding: "40px 48px 80px",
    }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ marginBottom: 24 }}>
          <Link
            to="/knowledge-feed"
            style={{
              color: T.accent, fontSize: 13, textDecoration: "none",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <ChevronLeft style={{ width: 14, height: 14 }} /> Knowledge Feed
          </Link>
        </div>

        {error ? (
          <div style={{
            padding: "16px 20px", color: T.red, background: T.redSoft,
            border: `1px solid ${T.red}`, borderRadius: 8, fontSize: 13,
          }}>
            Failed to load video <code>{videoId}</code> — {error}
          </div>
        ) : row === null ? (
          <div style={{
            padding: 24, fontSize: 14, color: T.ink3,
            display: "inline-flex", alignItems: "center", gap: 8,
          }}>
            <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
            Loading…
          </div>
        ) : (
          <>
            <Hero row={row} />

            <Embed videoId={row.video_id} />

            {/* Action bar — outbound link + KB cross-link */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
              <a
                href={watchUrl(row)}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontSize: 13, fontWeight: 600,
                  color: T.white, background: T.skyDark,
                  border: `1px solid ${T.skyDark}`,
                  padding: "8px 14px", borderRadius: 8,
                  textDecoration: "none",
                }}
              >
                <Youtube style={{ width: 14, height: 14 }} />
                Open on YouTube
                <ExternalLink style={{ width: 12, height: 12, opacity: 0.85 }} />
              </a>
              {row.kb_page_slug ? (
                <Link
                  to={`/${row.kb_page_slug.replace(/^\/+/, "")}`}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    fontSize: 13, fontWeight: 600,
                    color: T.skyDark, background: T.white,
                    border: `1px solid ${T.skyDark}`,
                    padding: "8px 14px", borderRadius: 8,
                    textDecoration: "none",
                  }}
                >
                  <FileText style={{ width: 14, height: 14 }} />
                  Open KB page
                </Link>
              ) : null}
            </div>

            <TagsRow topic={row.topic} tags={row.tags} />

            {row.ingest_error ? (
              <div style={{
                marginTop: 24,
                padding: "14px 18px",
                color: T.red, background: T.redSoft,
                border: `1px solid ${T.red}`, borderRadius: 8, fontSize: 13,
                display: "flex", alignItems: "flex-start", gap: 10,
              }}>
                <AlertTriangle style={{ width: 14, height: 14, marginTop: 2, flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Ingest error</div>
                  <div>{row.ingest_error}</div>
                </div>
              </div>
            ) : null}

            <SectionTitle icon={Sparkles} text="Summary" />
            {row.summary ? (
              <div style={{
                background: T.white,
                border: `1px solid ${T.line}`,
                borderRadius: 10,
                padding: "20px 24px",
                color: T.ink, fontSize: 15, lineHeight: 1.7,
                whiteSpace: "pre-wrap",
              }}>
                {row.summary}
              </div>
            ) : (
              <div style={{
                color: T.ink3, fontStyle: "italic", fontSize: 14,
                background: T.white, border: `1px dashed ${T.line}`,
                borderRadius: 10, padding: "16px 20px",
              }}>
                No summary yet — the ingest routine fills this in once the transcript has been processed.
              </div>
            )}

            {Array.isArray(row.principles) && row.principles.length > 0 && (
              <>
                <SectionTitle icon={Lightbulb} text="Learning principles" />
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {row.principles.map((p, i) => (
                    <div
                      key={i}
                      style={{
                        background: T.white,
                        border: `1px solid ${T.line}`,
                        borderRadius: 10,
                        padding: "14px 18px",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 14, color: T.ink, marginBottom: 4 }}>
                        {p.title}
                      </div>
                      <div style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.6 }}>
                        {p.snippet}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <SectionTitle icon={Brain} text="Transcript" />
            {row.transcript ? (
              <div style={{
                background: T.white,
                border: `1px solid ${T.line}`,
                borderRadius: 10,
                padding: "18px 22px",
                maxHeight: 520,
                overflowY: "auto",
                color: T.ink2, fontSize: 14, lineHeight: 1.7,
                whiteSpace: "pre-wrap",
                fontFamily:
                  "ui-sans-serif, system-ui, -apple-system, Inter, sans-serif",
              }}>
                {row.transcript}
              </div>
            ) : (
              <div style={{
                color: T.ink3, fontStyle: "italic", fontSize: 14,
                background: T.white, border: `1px dashed ${T.line}`,
                borderRadius: 10, padding: "16px 20px",
              }}>
                {row.has_transcript
                  ? "Transcript captured but not yet stored in this row."
                  : "No transcript was captured for this video."}
              </div>
            )}

            <div style={{
              marginTop: 48,
              paddingTop: 18,
              borderTop: `1px dashed ${T.line}`,
              fontSize: 11,
              color: T.ink3,
              textAlign: "center",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}>
              Video <code style={{
                color: T.accent, background: T.skySoft,
                fontFamily: "ui-monospace, Menlo, monospace",
                fontSize: 11, padding: "2px 6px", borderRadius: 4,
                textTransform: "none", letterSpacing: 0,
              }}>{row.video_id}</code>
              {" · "}Ingested by {row.agent}
              {" · "}Updated {new Date(row.updated_at).toLocaleString()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

(KnowledgeFeedDetailPage as unknown as { path: string }).path = "/knowledge-feed/:videoId";
